import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useLocale } from '@/hooks/use-locale'
import { beginBackNavigation, previousTrailEntry } from '@/hooks/use-navigation-trail'
import { nearestBreadcrumbParent, resolveBackTarget } from '@/utils/back-navigation.utils'
import { ArrowLeft } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router'

/**
 * Header back button.
 * Returns to the previous in-app page. A page opened directly (no history)
 * falls back to its breadcrumb parent, then the dashboard.
 */
export const BackButton = () => {
  const { t } = useLocale()
  const navigate = useNavigate()
  const location = useLocation()
  const normalizedPath = location.pathname.replace(/\/+$/, '') || '/'

  if (normalizedPath === '/') return null

  const handleBack = () => {
    const previous = previousTrailEntry()
    if (previous && previous.pathname !== normalizedPath) {
      const target = resolveBackTarget({
        pathname: normalizedPath,
        search: location.search,
        previous,
        t,
      })
      beginBackNavigation()
      navigate({ pathname: target.pathname, search: target.search }, { replace: true })
      return
    }

    // Trail has not recorded a prior page yet. Use browser history before the dashboard.
    if (location.key !== 'default') {
      navigate(-1)
      return
    }

    navigate(nearestBreadcrumbParent(normalizedPath, location.search, t) ?? '/')
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button id='nav-back' variant='ghost' size='icon' className='size-7 cursor-pointer' onClick={handleBack}>
          <ArrowLeft />
          <span className='sr-only'>Go back</span>
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <p>Go back</p>
      </TooltipContent>
    </Tooltip>
  )
}

export default BackButton
